import { useEffect, useMemo, useState } from "react"
import { createFileRoute, Navigate } from "@tanstack/react-router"
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  CalendarDays,
  Check,
  Copy,
  Loader2,
  Lock,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Ticket,
  Trash2,
} from "lucide-react"
import { toast } from "sonner"
import { requireSession } from "@/lib/auth-guard"
import { isControlPlaneUser } from "@/core/auth"
import {
  compPackage,
  createTenantEvent,
  deleteTenantEvent,
  deleteTenant,
  previewCompPackage,
  previewDeleteTenant,
  provisionSandbox,
  provisionTenant,
  setRegistrationPlatformFee,
  slugify,
  slugifyLive,
  updateTenant,
  useTenants,
  type CompPackagePreview,
  type DeleteTenantPreview,
  type ProvisionedSandbox,
  type ProvisionedTenant,
  type ProvisionInput,
  type Tenant,
  type TenantEvent,
} from "@/core/platform"
import { dollarsToCents, formatMoney } from "@/core/payments"
import { AccountMenu } from "@/components/org/AccountMenu"

export const Route = createFileRoute("/platform")({
  beforeLoad: async ({ location, context }) => {
    const user = await requireSession(location.href)
    context.queryClient.setQueryData(["session", "me"], user)
    return { user }
  },
  component: Platform,
})

// Sysadmin-only platform console: list every tenant and provision new ones
// (org + owner + first event). Signed-in is enforced in beforeLoad; the sysadmin
// gate runs client-side off the JWT `userRole` claim (the backend control-plane
// actions are independently role-gated, so this is a visibility guard).
function Platform() {
  const [gate, setGate] = useState<"checking" | "ok" | "denied">("checking")

  useEffect(() => {
    let alive = true
    void isControlPlaneUser().then((ok) => {
      if (alive) setGate(ok ? "ok" : "denied")
    })
    return () => {
      alive = false
    }
  }, [])

  if (gate === "checking") {
    return (
      <div className="app-canvas grid min-h-dvh place-items-center text-plum-700/40 dark:text-plum-200/30">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    )
  }

  if (gate === "denied") {
    return <Navigate to="/" replace />
  }

  return <PlatformConsole />
}

function PlatformConsole() {
  const tenants = useTenants((s) => s.tenants)
  const loading = useTenants((s) => s.loading)
  const loaded = useTenants((s) => s.loaded)
  const error = useTenants((s) => s.error)
  const load = useTenants((s) => s.load)
  const reload = useTenants((s) => s.reload)

  const [creating, setCreating] = useState(false)
  const [creatingSandbox, setCreatingSandbox] = useState(false)
  const [eventForOrg, setEventForOrg] = useState<Tenant | null>(null)
  const [editForOrg, setEditForOrg] = useState<Tenant | null>(null)
  const [deleteForOrg, setDeleteForOrg] = useState<Tenant | null>(null)

  // Only one top panel open at a time.
  function openPanel(which: "event" | "edit" | "delete", t: Tenant) {
    setCreating(false)
    setCreatingSandbox(false)
    setEventForOrg(which === "event" ? t : null)
    setEditForOrg(which === "edit" ? t : null)
    setDeleteForOrg(which === "delete" ? t : null)
  }
  function closePanels() {
    setCreating(false)
    setCreatingSandbox(false)
    setEventForOrg(null)
    setEditForOrg(null)
    setDeleteForOrg(null)
  }

  useEffect(() => {
    if (!loaded && !loading) void load()
  }, [loaded, loading, load])

  return (
    <div className="app-canvas min-h-dvh">
      <div className="mx-auto max-w-4xl px-6 py-10">
        <header className="mb-8 flex items-center gap-3">
          <img
            src="https://files.attend.vip/logo/logo_extra-small.svg"
            alt="Attend"
            className="h-3 w-auto dark:brightness-0 dark:invert"
          />
          <div className="flex-1">
            <h1 className="flex items-center gap-2 text-[20px] font-semibold tracking-tightish text-plum-900 dark:text-plum-50">
              Platform
              <span className="inline-flex items-center gap-1 rounded-full bg-plum-100/80 px-2 py-0.5 text-[10px] font-bold tracking-[0.06em] text-plum-700/70 uppercase dark:bg-white/10 dark:text-plum-200/60">
                <Lock className="h-2.5 w-2.5" /> Control plane
              </span>
            </h1>
            <p className="text-[13px] text-plum-700/55 dark:text-plum-200/45">
              Every tenant on Attend. Provision organizations and manage their events.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void reload()}
            disabled={loading}
            title="Refresh"
            className="grid h-9 w-9 place-items-center rounded-xl border border-plum-200/60 bg-white text-plum-700/60 transition hover:text-plum-900 disabled:opacity-50 dark:border-white/10 dark:bg-surface-raised dark:text-plum-200/50 dark:hover:text-plum-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <AccountMenu />
        </header>

        {creating ? (
          <ProvisionForm
            onCancel={() => setCreating(false)}
            onDone={() => {
              setCreating(false)
              void reload()
            }}
          />
        ) : creatingSandbox ? (
          <SandboxProvisionForm
            onCancel={() => setCreatingSandbox(false)}
            onDone={() => {
              setCreatingSandbox(false)
              void reload()
            }}
          />
        ) : eventForOrg ? (
          <TenantEventForm
            tenant={eventForOrg}
            onCancel={closePanels}
            onDone={() => {
              closePanels()
              void reload()
            }}
          />
        ) : editForOrg ? (
          <TenantEditForm
            tenant={editForOrg}
            onCancel={closePanels}
            onDone={() => {
              closePanels()
              void reload()
            }}
          />
        ) : deleteForOrg ? (
          <TenantDeletePanel
            tenant={deleteForOrg}
            onCancel={closePanels}
            onDone={() => {
              closePanels()
              void reload()
            }}
          />
        ) : (
          <div className="mb-6 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-plum-700 px-3.5 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:bg-plum-800"
            >
              <Plus className="h-4 w-4" /> New organization
            </button>
            <button
              type="button"
              onClick={() => setCreatingSandbox(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-plum-200/70 bg-white px-3.5 py-2 text-[13px] font-semibold text-plum-800 shadow-fluent-sm transition hover:bg-plum-50 dark:border-white/10 dark:bg-surface-raised dark:text-plum-100 dark:hover:bg-white/5"
            >
              <Ticket className="h-4 w-4" /> New sandbox
            </button>
          </div>
        )}

        <div className="mb-2 flex items-center gap-2 px-1">
          <Building2 className="h-3.5 w-3.5 text-plum-700/45 dark:text-plum-200/35" />
          <h2 className="text-[11px] font-bold tracking-[0.07em] text-plum-700/55 uppercase dark:text-plum-200/40">
            {loaded ? `${tenants.length} ${tenants.length === 1 ? "tenant" : "tenants"}` : "Tenants"}
          </h2>
        </div>

        {/* Not `loading && !loaded`: the load starts in an effect, so `loading`
            is false on the first render and the page flashed "no tenants".
            SSR keeps the store empty by design, so render the skeleton there. */}
        {import.meta.env.SSR || !loaded ? (
          <ul className="grid gap-2.5 sm:grid-cols-2">
            {[0, 1, 2, 3].map((i) => (
              <li key={i}>
                <div className="h-[72px] animate-pulse rounded-2xl border border-plum-200/60 bg-white dark:border-white/10 dark:bg-surface-raised" />
              </li>
            ))}
          </ul>
        ) : error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-[13px] text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
            Couldn't load tenants: {error}
          </div>
        ) : tenants.length === 0 ? (
          <div className="rounded-2xl border border-plum-200/60 bg-white p-8 text-center text-[13px] text-plum-700/55 shadow-fluent ring-1 ring-plum-200/30 dark:border-white/10 dark:bg-surface-raised dark:text-plum-200/45 dark:ring-white/5">
            No organizations yet.
          </div>
        ) : (
          <ul className="grid gap-2.5 sm:grid-cols-2">
            {tenants.map((t) => (
              <TenantCard
                key={t.organizationId}
                tenant={t}
                onCreateEvent={(tn) => openPanel("event", tn)}
                onEdit={(tn) => openPanel("edit", tn)}
                onDelete={(tn) => openPanel("delete", tn)}
                onEventChanged={() => void reload()}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

function TenantCard({
  tenant: t,
  onCreateEvent,
  onEdit,
  onDelete,
  onEventChanged,
}: {
  tenant: Tenant
  onCreateEvent: (t: Tenant) => void
  onEdit: (t: Tenant) => void
  onDelete: (t: Tenant) => void
  onEventChanged: () => void
}) {
  return (
    <li className="rounded-2xl border border-plum-200/60 bg-white p-3.5 shadow-fluent-sm ring-1 ring-plum-200/20 dark:border-white/10 dark:bg-surface-raised dark:ring-white/5">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-plum-500 to-plum-800 text-[12px] font-bold text-white">
          {initials(t.name)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[14px] font-semibold text-plum-900 dark:text-plum-50">{t.name}</p>
          <p className="truncate text-[11.5px] text-plum-700/50 dark:text-plum-200/40">/{t.slug}</p>
          {t.owner && (
            <p className="mt-0.5 truncate text-[11.5px] text-plum-700/55 dark:text-plum-200/45">
              {t.owner.name ?? t.owner.email}
            </p>
          )}
        </div>
        <span className="flex shrink-0 items-center gap-1 rounded-full bg-plum-100/70 px-2 py-0.5 text-[11px] font-semibold text-plum-700/70 dark:bg-white/10 dark:text-plum-200/55">
          <CalendarDays className="h-3 w-3" /> {t.eventCount}
        </span>
      </div>

      {t.events.length > 0 && (
        <ul className="mt-2.5 space-y-1 border-t border-plum-200/40 pt-2.5 dark:border-white/5">
          {t.events.slice(0, 4).map((ev) => (
            <TenantEventRow key={ev.eventId} event={ev} onEventChanged={onEventChanged} />
          ))}
          {t.events.length > 4 && (
            <li className="text-[11px] text-plum-700/40 dark:text-plum-200/30">
              +{t.events.length - 4} more
            </li>
          )}
        </ul>
      )}

      <div className="mt-2.5 flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onCreateEvent(t)}
          className="inline-flex items-center gap-1 rounded-lg border border-plum-200/60 px-2.5 py-1 text-[12px] font-semibold text-plum-700 transition hover:bg-plum-50 dark:border-white/10 dark:text-plum-200/70 dark:hover:bg-white/5"
        >
          <Plus className="h-3 w-3" /> Add event
        </button>
        <button
          type="button"
          onClick={() => onEdit(t)}
          title="Rename"
          className="inline-flex items-center gap-1 rounded-lg border border-plum-200/60 px-2.5 py-1 text-[12px] font-semibold text-plum-700 transition hover:bg-plum-50 dark:border-white/10 dark:text-plum-200/70 dark:hover:bg-white/5"
        >
          <Pencil className="h-3 w-3" /> Edit
        </button>
        <button
          type="button"
          onClick={() => onDelete(t)}
          title="Delete tenant"
          className="ml-auto inline-flex items-center gap-1 rounded-lg border border-rose-200 px-2.5 py-1 text-[12px] font-semibold text-rose-600 transition hover:bg-rose-50 dark:border-rose-500/20 dark:text-rose-300 dark:hover:bg-rose-500/10"
        >
          <Trash2 className="h-3 w-3" /> Delete
        </button>
      </div>
    </li>
  )
}

function TenantEventRow({
  event,
  onEventChanged,
}: {
  event: TenantEvent
  onEventChanged: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [fee, setFee] = useState((event.registrationPlatformFeeCents / 100).toFixed(2))
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const cents = dollarsToCents(fee)
  const valid = cents != null && cents >= 0

  // Enable-ticketing control. compPackage is enable-only (comp → "comped"), so
  // this is a one-way switch: preview the current status, confirm, comp.
  const [tkStage, setTkStage] = useState<"idle" | "confirm" | "done">("idle")
  const [tkPreview, setTkPreview] = useState<CompPackagePreview | null>(null)
  const [tkBusy, setTkBusy] = useState(false)
  const TK_REASON = "Enabled from platform console"

  async function startTicketing() {
    setTkBusy(true)
    try {
      const p = await previewCompPackage(event.eventId, "ticketing", TK_REASON)
      if (p.currentStatus === "comped" || p.currentStatus === "paid") {
        setTkStage("done")
        toast.info(`Ticketing already ${p.currentStatus} on ${event.name}`)
      } else {
        setTkPreview(p)
        setTkStage("confirm")
      }
    } catch (e) {
      // Already-paid packages make even the dryRun throw (ALREADY_PAID).
      toast.error(e instanceof Error ? e.message : "Couldn't check ticketing status")
    } finally {
      setTkBusy(false)
    }
  }

  async function confirmTicketing() {
    setTkBusy(true)
    try {
      await compPackage(event.eventId, "ticketing", TK_REASON)
      toast.success(`Ticketing enabled for ${event.name}`)
      setTkStage("done")
      setTkPreview(null)
      onEventChanged()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to enable ticketing")
    } finally {
      setTkBusy(false)
    }
  }

  useEffect(() => {
    if (!editing) setFee((event.registrationPlatformFeeCents / 100).toFixed(2))
  }, [editing, event.registrationPlatformFeeCents])

  async function save() {
    if (!valid || cents == null) {
      toast.error("Fee must be a valid dollar amount")
      return
    }
    setSubmitting(true)
    try {
      await setRegistrationPlatformFee({
        eventId: event.eventId,
        registrationPlatformFeeCents: cents,
      })
      toast.success("Registration fee updated")
      setEditing(false)
      onEventChanged()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update fee")
    } finally {
      setSubmitting(false)
    }
  }

  async function remove() {
    if (!confirm(`Delete "${event.name}"? This removes it from the tenant's event list.`)) return
    setDeleting(true)
    try {
      await deleteTenantEvent(event.eventId)
      toast.success("Event deleted")
      onEventChanged()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete event")
    } finally {
      setDeleting(false)
    }
  }

  return (
    <li className="grid gap-2 text-[12px] text-plum-700/65 dark:text-plum-200/45">
      <div className="flex items-center gap-2">
        <CalendarDays className="h-3 w-3 shrink-0 opacity-60" />
        <span className="truncate">{event.name}</span>
        <span className="ml-auto shrink-0 text-[10.5px] text-plum-700/40 dark:text-plum-200/30">
          /{event.slug}
        </span>
      </div>
      <div className="ml-5 flex min-w-0 items-center gap-2">
        {editing ? (
          <>
            <input
              className={`${inputCls} h-8 max-w-[120px] px-2 py-1 text-[12px] ${fee && !valid ? "border-rose-400" : ""}`}
              inputMode="decimal"
              value={fee}
              onChange={(e) => setFee(e.target.value)}
              aria-label={`${event.name} registration fee`}
            />
            <button
              type="button"
              onClick={() => void save()}
              disabled={!valid || submitting || cents === event.registrationPlatformFeeCents}
              className="inline-flex h-8 items-center gap-1 rounded-lg bg-plum-700 px-2.5 text-[12px] font-semibold text-white transition hover:bg-plum-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={submitting}
              className="h-8 rounded-lg px-2 text-[12px] font-semibold text-plum-700/60 hover:text-plum-900 disabled:opacity-50 dark:text-plum-200/50 dark:hover:text-plum-50"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className="truncate text-[11.5px] text-plum-700/50 dark:text-plum-200/40">
              Attend fee {formatMoney(event.registrationPlatformFeeCents, "usd")}
            </span>
            {tkStage === "done" ? (
              <span className="ml-auto inline-flex items-center gap-1 rounded-lg border border-emerald-200 px-2 py-0.5 text-[11.5px] font-semibold text-emerald-600 dark:border-emerald-500/20 dark:text-emerald-300">
                <Check className="h-3 w-3" /> Ticketing on
              </span>
            ) : (
              <button
                type="button"
                onClick={() => void startTicketing()}
                disabled={tkBusy || tkStage === "confirm"}
                title="Enable ticket sales & registration for this event"
                className="ml-auto inline-flex items-center gap-1 rounded-lg border border-plum-200/60 px-2 py-0.5 text-[11.5px] font-semibold text-plum-700 transition hover:bg-plum-50 disabled:opacity-50 dark:border-white/10 dark:text-plum-200/70 dark:hover:bg-white/5"
              >
                {tkBusy && tkStage === "idle" ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Ticket className="h-3 w-3" />
                )}
                Ticketing
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1 rounded-lg border border-plum-200/60 px-2 py-0.5 text-[11.5px] font-semibold text-plum-700 transition hover:bg-plum-50 dark:border-white/10 dark:text-plum-200/70 dark:hover:bg-white/5"
            >
              <Pencil className="h-3 w-3" /> Fee
            </button>
            <button
              type="button"
              onClick={() => void remove()}
              disabled={deleting}
              className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-2 py-0.5 text-[11.5px] font-semibold text-rose-600 transition hover:bg-rose-50 disabled:opacity-50 dark:border-rose-500/20 dark:text-rose-300 dark:hover:bg-rose-500/10"
            >
              {deleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              Delete
            </button>
          </>
        )}
      </div>

      {tkStage === "confirm" && tkPreview && (
        <div className="ml-5 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-2.5 py-1.5 text-[11.5px] text-plum-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-plum-100">
          <span>
            Enable ticketing? <strong>{tkPreview.currentStatus}</strong> → <strong>comped</strong>.
            Comps the Ticket Sales package (no charge).
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => void confirmTicketing()}
              disabled={tkBusy}
              className="inline-flex items-center gap-1 rounded-lg bg-plum-700 px-2.5 py-1 text-[11.5px] font-semibold text-white transition hover:bg-plum-800 disabled:opacity-50"
            >
              {tkBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              Confirm
            </button>
            <button
              type="button"
              onClick={() => {
                setTkStage("idle")
                setTkPreview(null)
              }}
              disabled={tkBusy}
              className="rounded-lg px-2 py-1 text-[11.5px] font-semibold text-plum-700/60 hover:text-plum-900 disabled:opacity-50 dark:text-plum-200/50 dark:hover:text-plum-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  )
}

// Rename a tenant (name + slug). The form fields are the preview, so this calls
// updateTenant directly rather than the dryRun path.
function TenantEditForm({
  tenant,
  onCancel,
  onDone,
}: {
  tenant: Tenant
  onCancel: () => void
  onDone: () => void
}) {
  const [name, setName] = useState(tenant.name)
  const [slug, setSlug] = useState(tenant.slug)
  const [submitting, setSubmitting] = useState(false)

  const dirty = name.trim() !== tenant.name || slug.trim() !== tenant.slug
  const err = useMemo(() => {
    if (!name.trim()) return "Name is required"
    if (!/^[a-z0-9-]+$/.test(slug)) return "Slug is invalid"
    return null
  }, [name, slug])

  async function save() {
    if (err) {
      toast.error(err)
      return
    }
    setSubmitting(true)
    try {
      const cleanSlug = slugify(slug)
      await updateTenant({
        organizationId: tenant.organizationId,
        ...(name.trim() !== tenant.name ? { name: name.trim() } : {}),
        ...(cleanSlug !== tenant.slug ? { slug: cleanSlug } : {}),
      })
      toast.success("Tenant updated")
      onDone()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update tenant")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mb-6 rounded-2xl border border-plum-200/60 bg-white p-5 shadow-fluent ring-1 ring-plum-200/25 dark:border-white/10 dark:bg-surface-raised dark:ring-white/5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">Edit tenant</h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-[12.5px] font-medium text-plum-700/55 hover:text-plum-900 dark:text-plum-200/45 dark:hover:text-plum-50"
        >
          Cancel
        </button>
      </div>
      <div className="space-y-4">
        <Field label="Name">
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Slug" hint="changing this changes the org's URLs">
          <input
            className={inputCls}
            value={slug}
            onChange={(e) => setSlug(slugifyLive(e.target.value))}
          />
        </Field>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() => void save()}
            disabled={!dirty || !!err || submitting}
            className="inline-flex items-center gap-2 rounded-xl bg-plum-700 px-4 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:bg-plum-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Save changes
          </button>
        </div>
      </div>
    </div>
  )
}

// Delete a tenant. Runs the dryRun preview on open; the backend refuses (409
// ORG_HAS_EVENTS) when events still exist, which we surface as a hard block.
function TenantDeletePanel({
  tenant,
  onCancel,
  onDone,
}: {
  tenant: Tenant
  onCancel: () => void
  onDone: () => void
}) {
  const [stage, setStage] = useState<"loading" | "ready" | "blocked">("loading")
  const [preview, setPreview] = useState<DeleteTenantPreview | null>(null)
  const [blockMsg, setBlockMsg] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    let alive = true
    previewDeleteTenant(tenant.organizationId)
      .then((p) => {
        if (alive) {
          setPreview(p)
          setStage("ready")
        }
      })
      .catch((e) => {
        if (alive) {
          setBlockMsg(e instanceof Error ? e.message : "This tenant can't be deleted")
          setStage("blocked")
        }
      })
    return () => {
      alive = false
    }
  }, [tenant.organizationId])

  async function confirmDelete() {
    setDeleting(true)
    try {
      await deleteTenant(tenant.organizationId)
      toast.success(`Deleted ${tenant.name}`)
      onDone()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete tenant")
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="mb-6 rounded-2xl border border-rose-200 bg-rose-50/50 p-5 ring-1 ring-rose-200/40 dark:border-rose-500/20 dark:bg-rose-500/10 dark:ring-rose-500/10">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-rose-500/15 text-rose-600 dark:text-rose-300">
          <AlertTriangle className="h-4 w-4" />
        </span>
        <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
          Delete {tenant.name}?
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="ml-auto text-[12.5px] font-medium text-plum-700/55 hover:text-plum-900 dark:text-plum-200/45 dark:hover:text-plum-50"
        >
          Cancel
        </button>
      </div>

      {stage === "loading" ? (
        <div className="flex items-center gap-2 text-[13px] text-plum-700/55 dark:text-plum-200/45">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking what would be removed…
        </div>
      ) : stage === "blocked" ? (
        <div className="space-y-3">
          <p className="text-[13px] leading-snug text-rose-700 dark:text-rose-300">{blockMsg}</p>
          <p className="text-[12px] leading-snug text-plum-700/55 dark:text-plum-200/45">
            Delete the tenant's events first (events and guests live in a separate database and
            won't be removed automatically).
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-[13px] leading-snug text-plum-800 dark:text-plum-100">
            This permanently removes the organization and{" "}
            <strong>{preview?.authRows.members ?? 0}</strong> member
            {preview?.authRows.members === 1 ? "" : "s"} +{" "}
            <strong>{preview?.authRows.invitations ?? 0}</strong> pending invite
            {preview?.authRows.invitations === 1 ? "" : "s"}. This can't be undone.
          </p>
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => void confirmDelete()}
              disabled={deleting}
              className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:bg-rose-700 disabled:opacity-60"
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
              Delete tenant
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Single-step event form for an existing tenant. createTenantEvent is flat-input
// (MCP-safe) and creates the event under the chosen org regardless of membership.
function TenantEventForm({
  tenant,
  onCancel,
  onDone,
}: {
  tenant: Tenant
  onCancel: () => void
  onDone: () => void
}) {
  const [name, setName] = useState("")
  const [slug, setSlug] = useState("")
  const [slugTouched, setSlugTouched] = useState(false)
  const [venue, setVenue] = useState("")
  const [startsAt, setStartsAt] = useState("")
  const [endsAt, setEndsAt] = useState("")
  const [tz, setTz] = useState("")
  const [fee, setFee] = useState("5.00")
  const [submitting, setSubmitting] = useState(false)
  const feeCents = dollarsToCents(fee)

  const err = useMemo(() => {
    if (!name.trim()) return "Event name is required"
    if (!/^[a-z0-9-]+$/.test(slug)) return "Event slug is invalid"
    if (!startsAt || startsAt.length < 16) return "Start is required"
    if (!endsAt || endsAt.length < 16) return "End is required"
    if (endsAt <= startsAt) return "End must be after the start"
    if (feeCents == null || feeCents < 0) return "Registration fee is invalid"
    return null
  }, [name, slug, startsAt, endsAt, feeCents])

  async function submit() {
    if (err) {
      toast.error(err)
      return
    }
    setSubmitting(true)
    try {
      const ev = await createTenantEvent({
        organizationId: tenant.organizationId,
        name: name.trim(),
        slug: slugify(slug),
        startsAt,
        endsAt,
        registrationPlatformFeeCents: feeCents ?? 500,
        ...(venue.trim() ? { venueName: venue.trim() } : {}),
        ...(tz.trim() ? { ianaTimezone: tz.trim() } : {}),
      })
      toast.success(`Created ${ev.name} for ${tenant.name}`)
      onDone()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create event")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mb-6 rounded-2xl border border-plum-200/60 bg-white p-5 shadow-fluent ring-1 ring-plum-200/25 dark:border-white/10 dark:bg-surface-raised dark:ring-white/5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
          New event &middot; {tenant.name}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-[12.5px] font-medium text-plum-700/55 hover:text-plum-900 dark:text-plum-200/45 dark:hover:text-plum-50"
        >
          Cancel
        </button>
      </div>
      <div className="space-y-4">
        <Field label="Name">
          <input
            className={inputCls}
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (!slugTouched) setSlug(slugify(e.target.value))
            }}
            placeholder="Acme Summit 2027"
          />
        </Field>
        <Field label="Slug">
          <input
            className={inputCls}
            value={slug}
            onChange={(e) => {
              setSlugTouched(true)
              setSlug(slugifyLive(e.target.value))
            }}
            placeholder="acme-summit-2027"
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Starts">
            <input
              className={inputCls}
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </Field>
          <Field label="Ends">
            <input
              className={inputCls}
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Venue" hint="optional">
            <input
              className={inputCls}
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              placeholder="The Grand Hotel"
            />
          </Field>
          <Field label="Timezone" hint="optional, IANA">
            <input
              className={inputCls}
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              placeholder="America/New_York"
            />
          </Field>
        </div>
        <Field label="Attend fee" hint="per paid registration">
          <input
            className={`${inputCls} ${fee && feeCents == null ? "border-rose-400" : ""}`}
            inputMode="decimal"
            value={fee}
            onChange={(e) => setFee(e.target.value)}
            placeholder="5.00"
          />
        </Field>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!!err || submitting}
            className="inline-flex items-center gap-2 rounded-xl bg-plum-700 px-4 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:bg-plum-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Create event
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Provisioning form: edit → review → confirm. Never assume — the review step
// shows exactly what will be created before the single irreversible POST.
// ---------------------------------------------------------------------------

type FormState = {
  orgName: string
  orgSlug: string
  ownerName: string
  ownerEmail: string
}

const EMPTY: FormState = {
  orgName: "",
  orgSlug: "",
  ownerName: "",
  ownerEmail: "",
}

function ProvisionForm({ onCancel, onDone }: { onCancel: () => void; onDone: () => void }) {
  const [f, setF] = useState<FormState>(EMPTY)
  // Track whether the slug was hand-edited so we stop auto-deriving it.
  const [orgSlugTouched, setOrgSlugTouched] = useState(false)
  const [mode, setMode] = useState<"edit" | "review">("edit")
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<ProvisionedTenant | null>(null)

  const set = (patch: Partial<FormState>) => setF((p) => ({ ...p, ...patch }))

  const validation = useMemo(() => validate(f), [f])

  if (result) {
    return <ProvisionResult result={result} onClose={onDone} />
  }

  function toReview() {
    if (validation) {
      toast.error(validation)
      return
    }
    setMode("review")
  }

  async function confirm() {
    setSubmitting(true)
    try {
      const input = buildInput(f)
      const tenant = await provisionTenant(input)
      toast.success(`Created ${tenant.name}`)
      setResult(tenant)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to provision tenant")
      setMode("edit")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mb-6 rounded-2xl border border-plum-200/60 bg-white p-5 shadow-fluent ring-1 ring-plum-200/25 dark:border-white/10 dark:bg-surface-raised dark:ring-white/5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
          {mode === "edit" ? "New organization" : "Review & confirm"}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-[12.5px] font-medium text-plum-700/55 hover:text-plum-900 dark:text-plum-200/45 dark:hover:text-plum-50"
        >
          Cancel
        </button>
      </div>

      {mode === "edit" ? (
        <div className="space-y-5">
          <Section title="Organization">
            <Field label="Name">
              <input
                className={inputCls}
                value={f.orgName}
                onChange={(e) => {
                  const orgName = e.target.value
                  set({ orgName, ...(orgSlugTouched ? {} : { orgSlug: slugify(orgName) }) })
                }}
                placeholder="Acme Incentives"
              />
            </Field>
            <Field label="Slug" hint="lowercase, a–z 0–9 and hyphens">
              <input
                className={inputCls}
                value={f.orgSlug}
                onChange={(e) => {
                  setOrgSlugTouched(true)
                  set({ orgSlug: slugifyLive(e.target.value) })
                }}
                placeholder="acme-incentives"
              />
            </Field>
          </Section>

          <Section title="Owner">
            <Field label="Full name">
              <input
                className={inputCls}
                value={f.ownerName}
                onChange={(e) => set({ ownerName: e.target.value })}
                placeholder="Jane Doe"
              />
            </Field>
            <Field label="Email" hint="they'll get a welcome email to sign in">
              <input
                className={inputCls}
                type="email"
                value={f.ownerEmail}
                onChange={(e) => set({ ownerEmail: e.target.value })}
                placeholder="jane@acme.com"
              />
            </Field>
          </Section>

          <p className="text-[12px] leading-snug text-plum-700/55 dark:text-plum-200/45">
            This creates the organization and its owner only. The owner adds their own events from
            the console once they sign in.
          </p>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={toReview}
              disabled={!!validation}
              className="inline-flex items-center gap-2 rounded-xl bg-plum-700 px-4 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:bg-plum-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Review
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <ReviewBlock title="Organization" rows={[["Name", f.orgName], ["Slug", `/${f.orgSlug}`]]} />
          <ReviewBlock title="Owner" rows={[["Name", f.ownerName], ["Email", f.ownerEmail.trim().toLowerCase()]]} />
          <p className="text-[12px] leading-snug text-plum-700/55 dark:text-plum-200/45">
            This creates the org and the owner account (or links an existing one), then emails the
            owner a welcome with a sign-in link — they sign in with a one-time code, no password. No
            event is created — the owner adds events themselves.
          </p>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setMode("edit")}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 rounded-xl border border-plum-200/60 px-3.5 py-2 text-[13px] font-semibold text-plum-700 transition hover:bg-plum-50 disabled:opacity-50 dark:border-white/10 dark:text-plum-200 dark:hover:bg-white/5"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Edit
            </button>
            <button
              type="button"
              onClick={() => void confirm()}
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-xl bg-plum-700 px-4 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:bg-plum-800 disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Create organization
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function ProvisionResult({ result, onClose }: { result: ProvisionedTenant; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 ring-1 ring-emerald-200/40 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:ring-emerald-500/10">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-emerald-500 text-white">
          <Check className="h-4 w-4" />
        </span>
        <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
          {result.name} is live
        </h2>
      </div>
      <dl className="space-y-1.5 text-[13px]">
        <ResRow label="Org" value={`/${result.slug}`} />
        <ResRow label="Owner" value={`${result.owner.name} · ${result.owner.email}`} />
        <ResRow
          label="Owner account"
          value={result.existingUser ? "linked existing user" : "new user created"}
        />
        {result.event && (
          <ResRow label="Event" value={`${result.event.name} (/${result.event.slug})`} />
        )}
        <ResRow
          label="Welcome email"
          value={
            result.emailDelivery === "sent"
              ? `sent to ${result.owner.email}`
              : `failed — ${result.emailError ?? "unknown"}`
          }
        />
      </dl>
      <p className="mt-3 text-[12px] leading-snug text-plum-700/60 dark:text-plum-200/50">
        {result.emailDelivery === "sent"
          ? "We emailed the owner a welcome with this sign-in link — they sign in with a one-time code, no password. You can also share it directly:"
          : "The welcome email didn't send — share this sign-in link with the owner. They sign in with a one-time code, no password:"}
      </p>
      <div className="mt-2 flex items-center gap-2 rounded-xl border border-emerald-300/50 bg-white/70 p-2.5 dark:border-emerald-500/20 dark:bg-black/20">
        <code className="min-w-0 flex-1 truncate text-[12px] text-plum-800 dark:text-plum-100">
          {result.handoffUrl}
        </code>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(result.handoffUrl)
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          }}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-plum-700 px-2.5 py-1.5 text-[12px] font-semibold text-white transition hover:bg-plum-800"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? "Copied" : "Copy sign-in link"}
        </button>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl bg-plum-700 px-4 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:bg-plum-800"
        >
          Done
        </button>
      </div>
    </div>
  )
}

type SandboxPerson = { name: string; email: string }

type SandboxFormState = {
  orgName: string
  orgSlug: string
  people: SandboxPerson[]
  eventName: string
}

const EMPTY_SANDBOX: SandboxFormState = {
  orgName: "",
  orgSlug: "",
  people: [{ name: "", email: "" }],
  eventName: "Sales Kickoff 2027",
}

function SandboxProvisionForm({ onCancel, onDone }: { onCancel: () => void; onDone: () => void }) {
  const [f, setF] = useState<SandboxFormState>(EMPTY_SANDBOX)
  const [orgSlugTouched, setOrgSlugTouched] = useState(false)
  const [mode, setMode] = useState<"edit" | "review">("edit")
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<ProvisionedSandbox | null>(null)

  const set = (patch: Partial<SandboxFormState>) => setF((p) => ({ ...p, ...patch }))
  const validation = useMemo(() => validateSandbox(f), [f])

  if (result) {
    return <SandboxProvisionResult result={result} onClose={onDone} />
  }

  function toReview() {
    if (validation) {
      toast.error(validation)
      return
    }
    setMode("review")
  }

  async function confirm() {
    setSubmitting(true)
    try {
      const tenant = await provisionSandbox({
        orgName: f.orgName.trim(),
        orgSlug: slugify(f.orgSlug) || undefined,
        people: f.people.map((p) => ({ name: p.name.trim(), email: p.email.trim() })),
        eventName: f.eventName.trim() || undefined,
      })
      toast.success(`Created ${tenant.name}`)
      setResult(tenant)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to provision sandbox")
      setMode("edit")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mb-6 rounded-2xl border border-plum-200/60 bg-white p-5 shadow-fluent ring-1 ring-plum-200/25 dark:border-white/10 dark:bg-surface-raised dark:ring-white/5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
          {mode === "edit" ? "New sandbox" : "Review & confirm"}
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className="text-[12.5px] font-medium text-plum-700/55 hover:text-plum-900 dark:text-plum-200/45 dark:hover:text-plum-50"
        >
          Cancel
        </button>
      </div>

      {mode === "edit" ? (
        <div className="space-y-5">
          <Section title="Organization">
            <Field label="Name">
              <input
                className={inputCls}
                value={f.orgName}
                onChange={(e) => {
                  const orgName = e.target.value
                  set({ orgName, ...(orgSlugTouched ? {} : { orgSlug: slugify(orgName) }) })
                }}
                placeholder="Acme Incentives"
              />
            </Field>
            <Field label="Slug" hint="lowercase, a–z 0–9 and hyphens">
              <input
                className={inputCls}
                value={f.orgSlug}
                onChange={(e) => {
                  setOrgSlugTouched(true)
                  set({ orgSlug: slugifyLive(e.target.value) })
                }}
                placeholder="acme-incentives"
              />
            </Field>
          </Section>

          <Section title="People">
            <p className="text-[12px] leading-snug text-plum-700/55 dark:text-plum-200/45">
              First person is the owner. Everyone gets console + Attend VIP access.
            </p>
            <div className="space-y-2">
              {f.people.map((p, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="grid min-w-0 flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                    <input
                      className={inputCls}
                      value={p.name}
                      onChange={(e) => {
                        const people = f.people.slice()
                        people[i] = { ...p, name: e.target.value }
                        set({ people })
                      }}
                      placeholder={i === 0 ? "Owner name" : "Name"}
                      autoComplete="off"
                    />
                    <input
                      className={inputCls}
                      type="email"
                      value={p.email}
                      onChange={(e) => {
                        const people = f.people.slice()
                        people[i] = { ...p, email: e.target.value }
                        set({ people })
                      }}
                      placeholder={i === 0 ? "owner@acme.com" : "email@acme.com"}
                      autoComplete="off"
                    />
                  </div>
                  {f.people.length > 1 && (
                    <button
                      type="button"
                      title="Remove"
                      onClick={() => set({ people: f.people.filter((_, j) => j !== i) })}
                      className="mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-plum-700/45 transition hover:bg-plum-50 hover:text-plum-900 dark:hover:bg-white/5 dark:hover:text-plum-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {f.people.length < 20 && (
              <button
                type="button"
                onClick={() => set({ people: [...f.people, { name: "", email: "" }] })}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-plum-700 hover:text-plum-900 dark:text-plum-200 dark:hover:text-plum-50"
              >
                <Plus className="h-3.5 w-3.5" /> Add person
              </button>
            )}
          </Section>

          <Section title="Demo event">
            <Field label="Name" hint="seeded with Sales Kickoff content">
              <input
                className={inputCls}
                value={f.eventName}
                onChange={(e) => set({ eventName: e.target.value })}
                placeholder="Sales Kickoff 2027"
              />
            </Field>
          </Section>

          <p className="text-[12px] leading-snug text-plum-700/55 dark:text-plum-200/45">
            Creates the org, adds everyone as admins, seeds a sandbox event, confirms each person as
            a guest, and emails console + Attend VIP sign-in links.
          </p>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={toReview}
              disabled={!!validation}
              className="inline-flex items-center gap-2 rounded-xl bg-plum-700 px-4 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:bg-plum-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Review
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <ReviewBlock title="Organization" rows={[["Name", f.orgName], ["Slug", `/${slugify(f.orgSlug)}`]]} />
          <ReviewBlock
            title="People"
            rows={f.people.map((p, i) => [
              i === 0 ? "Owner" : "Admin",
              `${p.name.trim()} · ${p.email.trim().toLowerCase()}`,
            ])}
          />
          <ReviewBlock title="Event" rows={[["Name", f.eventName.trim() || "Sales Kickoff 2027"]]} />
          <p className="text-[12px] leading-snug text-plum-700/55 dark:text-plum-200/45">
            Each person is emailed a one-time-code sign-in for the organizer console and the Attend
            VIP app. They're already on the guest list.
          </p>
          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => setMode("edit")}
              disabled={submitting}
              className="inline-flex items-center gap-1.5 rounded-xl border border-plum-200/60 px-3.5 py-2 text-[13px] font-semibold text-plum-700 transition hover:bg-plum-50 disabled:opacity-50 dark:border-white/10 dark:text-plum-200 dark:hover:bg-white/5"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Edit
            </button>
            <button
              type="button"
              onClick={() => void confirm()}
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-xl bg-plum-700 px-4 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:bg-plum-800 disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Create sandbox
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function SandboxProvisionResult({ result, onClose }: { result: ProvisionedSandbox; onClose: () => void }) {
  const failed = result.emails.filter((e) => e.delivery === "failed")
  return (
    <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 ring-1 ring-emerald-200/40 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:ring-emerald-500/10">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-emerald-500 text-white">
          <Check className="h-4 w-4" />
        </span>
        <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
          {result.name} sandbox is live
        </h2>
      </div>
      <dl className="space-y-1.5 text-[13px]">
        <ResRow label="Org" value={`/${result.slug}`} />
        <ResRow label="Event" value={`${result.event.name} (/${result.event.slug})`} />
        {result.people.map((p) => (
          <ResRow
            key={p.userId}
            label={p.role === "owner" ? "Owner" : "Admin"}
            value={`${p.name} · ${p.email}${p.existingUser ? " (existing)" : ""}`}
          />
        ))}
        <ResRow
          label="Welcome email"
          value={
            failed.length === 0
              ? `sent to ${result.emails.length} ${result.emails.length === 1 ? "person" : "people"}`
              : `failed for ${failed.map((e) => e.email).join(", ")}`
          }
        />
      </dl>
      <p className="mt-3 text-[12px] leading-snug text-plum-700/60 dark:text-plum-200/50">
        Console and Attend VIP both use this email + a one-time code. They're already confirmed
        guests, so the event shows up as soon as they sign in.
      </p>
      <div className="mt-2 space-y-2">
        <CopyRow label="Console" url={result.consoleUrl} />
        <CopyRow label="Attend VIP" url={result.guestAppUrl} />
      </div>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl bg-plum-700 px-4 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:bg-plum-800"
        >
          Done
        </button>
      </div>
    </div>
  )
}

function CopyRow({ label, url }: { label: string; url: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex items-center gap-2 rounded-xl border border-emerald-300/50 bg-white/70 p-2.5 dark:border-emerald-500/20 dark:bg-black/20">
      <span className="w-20 shrink-0 text-[11px] font-semibold tracking-wide text-plum-700/55 uppercase dark:text-plum-200/45">
        {label}
      </span>
      <code className="min-w-0 flex-1 truncate text-[12px] text-plum-800 dark:text-plum-100">{url}</code>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText(url)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        }}
        className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-plum-700 px-2.5 py-1.5 text-[12px] font-semibold text-white transition hover:bg-plum-800"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  )
}

// ----- small presentational helpers ---------------------------------------

const inputCls =
  "w-full rounded-xl border border-plum-200/70 bg-white px-3 py-2 text-[13.5px] text-plum-900 outline-none transition placeholder:text-plum-700/30 focus:border-plum-400 focus:ring-2 focus:ring-plum-400/20 dark:border-white/10 dark:bg-black/20 dark:text-plum-50 dark:placeholder:text-plum-200/25"

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h3 className="text-[11px] font-bold tracking-[0.07em] text-plum-700/55 uppercase dark:text-plum-200/40">
        {title}
      </h3>
      {children}
    </div>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-baseline gap-2">
        <span className="text-[12.5px] font-semibold text-plum-800 dark:text-plum-100">{label}</span>
        {hint && <span className="text-[11px] text-plum-700/45 dark:text-plum-200/35">{hint}</span>}
      </span>
      {children}
    </label>
  )
}

function ReviewBlock({ title, rows }: { title: string; rows: [string, string][] }) {
  return (
    <div className="rounded-xl border border-plum-200/50 bg-plum-50/40 p-3 dark:border-white/10 dark:bg-black/20">
      <p className="mb-1.5 text-[11px] font-bold tracking-[0.07em] text-plum-700/55 uppercase dark:text-plum-200/40">
        {title}
      </p>
      <dl className="space-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-3 text-[13px]">
            <dt className="w-20 shrink-0 text-plum-700/55 dark:text-plum-200/40">{k}</dt>
            <dd className="min-w-0 flex-1 break-words font-medium text-plum-900 dark:text-plum-50">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function ResRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3">
      <dt className="w-32 shrink-0 text-plum-700/55 dark:text-plum-200/45">{label}</dt>
      <dd className="min-w-0 flex-1 break-words font-medium text-plum-900 dark:text-plum-50">{value}</dd>
    </div>
  )
}

// ----- validation + input mapping -----------------------------------------

function validate(f: FormState): string | null {
  if (!f.orgName.trim()) return "Organization name is required"
  if (!/^[a-z0-9-]+$/.test(f.orgSlug)) return "Organization slug is invalid"
  if (!f.ownerName.trim()) return "Owner name is required"
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.ownerEmail.trim())) return "A valid owner email is required"
  return null
}

function validateSandbox(f: SandboxFormState): string | null {
  if (!f.orgName.trim()) return "Organization name is required"
  if (f.orgSlug.trim() && !/^[a-z0-9-]+$/.test(slugify(f.orgSlug))) return "Organization slug is invalid"
  if (f.people.length === 0) return "Add at least one person"
  const emails = new Set<string>()
  for (const [i, p] of f.people.entries()) {
    const who = i === 0 ? "owner" : `person ${i + 1}`
    if (!p.name.trim()) return `A name is required for the ${who}`
    const email = p.email.trim()
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return `A valid email is required for the ${who}`
    const key = email.toLowerCase()
    if (emails.has(key)) return `${email} is listed twice`
    emails.add(key)
  }
  return null
}

function buildInput(f: FormState): ProvisionInput {
  return {
    organization: { name: f.orgName.trim(), slug: slugify(f.orgSlug) },
    owner: { name: f.ownerName.trim(), email: f.ownerEmail.trim() },
  }
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}
